/**
 * The blocking dialog — level three of D1, and the only part of this module that interrupts.
 *
 * It appears once, on the first load after a release that carries an entry marked `major`, and
 * never again. Everything else about the module is designed so that this is rare: the audience
 * opens this app in the minute before a customer call, and D7 exists precisely to keep `major`
 * from becoming wallpaper.
 *
 * ## Closing is the acknowledgement, whichever way it happened
 *
 * Esc, the backdrop and "Got it" all do the same thing: the entries are marked seen and the
 * dialog does not come back. A close that did not acknowledge would put the same dialog in
 * front of the same person tomorrow morning, which is the behaviour people learn to resent.
 *
 * Esc is bound on `window` rather than on the panel. As a React `onKeyDown` it worked only
 * while focus was inside the panel, so one click on the backdrop moved focus to `<body>` and
 * left "Got it" as the only way out of a dialog whose own documentation promised three
 * (`/code-review high`, W2).
 *
 * `dismissed` is local and deliberately not derived from the state. If the POST fails, the
 * provider rolls the optimistic update back — right for a marker, wrong for a dialog, which
 * would pop back up a second after being closed. It reappears on the next load instead, which
 * is the honest consequence of a write that did not happen.
 *
 * ## Accessibility is not optional here, because this thing takes the screen
 *
 * `role="dialog"`, `aria-modal`, a label that names the entry, Esc to close, focus moved in on
 * open, focus trapped while open, focus restored to wherever it came from on close.
 */

import { useEffect, useRef, useState } from 'react';
import { useWhatsNew } from './WhatsNewProvider';

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export function WhatsNewDialog() {
    const { majorEntries, markEntriesSeen, track } = useWhatsNew();
    // Keyed on WHICH entries are shown (`reporting_app`): the provider stays mounted across
    // routes here, and the major entries change with the page. A plain boolean would keep the
    // dialog closed for a different page's major entry until the next reload.
    const shownKey = majorEntries.map(e => e.id).join('|');
    const [dismissedKey, setDismissedKey] = useState<string | null>(null);
    const panel = useRef<HTMLDivElement>(null);
    const returnFocusTo = useRef<Element | null>(null);
    const announcedKey = useRef<string | null>(null);

    const open = dismissedKey !== shownKey && majorEntries.length > 0;

    useEffect(() => {
        if (!open || announcedKey.current === shownKey) return;
        announcedKey.current = shownKey;
        track('dialog-shown', { entryIds: majorEntries.map(e => e.id) });
    }, [open, shownKey, majorEntries, track]);

    useEffect(() => {
        if (!open) return;
        returnFocusTo.current = document.activeElement;
        // Focus the panel itself rather than the first button: a dialog that opens with an
        // action pre-selected invites the Enter key that was already on its way somewhere else.
        panel.current?.focus();
        return () => {
            (returnFocusTo.current as HTMLElement | null)?.focus?.();
        };
    }, [open]);

    const close = () => {
        setDismissedKey(shownKey);
        track('dialog-closed', { entryIds: majorEntries.map(e => e.id) });
        markEntriesSeen(majorEntries.map(e => e.id));
    };

    // Held in a ref so the window listener below never needs re-binding, and never closes over
    // a stale `majorEntries`.
    const closeRef = useRef(close);
    closeRef.current = close;

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeRef.current();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    if (!open) return null;

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        // The trap. Without it, Tab walks out of a dialog that is covering the page and lands
        // on controls the reader cannot see.
        const items = Array.from(panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || active === panel.current)) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
        }
    };

    const headingId = `whats-new-dialog-${majorEntries[0].id}`;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
            // The backdrop closes it, and therefore acknowledges it. `currentTarget` so that a
            // click inside the panel does not bubble out and dismiss what it was aimed at.
            onClick={e => { if (e.target === e.currentTarget) close(); }}
        >
            <div
                ref={panel}
                role="dialog"
                aria-modal="true"
                aria-labelledby={headingId}
                tabIndex={-1}
                onKeyDown={onKeyDown}
                className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl outline-none"
            >
                <p className="text-[11px] font-bold uppercase tracking-wide text-blue-600">What&apos;s New</p>

                {majorEntries.map((entry, index) => (
                    <div key={entry.id} className={index > 0 ? 'mt-4 border-t border-gray-200 pt-4' : 'mt-1'}>
                        <h2
                            id={index === 0 ? headingId : undefined}
                            className="text-lg font-bold text-gray-900"
                        >
                            {entry.title}
                        </h2>
                        <p className="mt-1 text-sm leading-relaxed text-gray-600">{entry.body}</p>
                    </div>
                ))}

                <div className="mt-5 flex justify-end">
                    <button
                        type="button"
                        onClick={close}
                        className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                    >
                        Got it
                    </button>
                </div>
            </div>
        </div>
    );
}
